import { useSyncExternalStore } from "react";
import { authApi, type ApiUser } from "./api/client";

// ── Role definitions ────────────────────────────────────────────────────────
export type Role = "admin" | "manager" | "cashier" | "waiter" | "viewer";

export type AuthUser = {
  id: string;
  username: string;
  displayName: string;
  role: Role;
};

type AuthState = {
  user: AuthUser | null;
};

const AUTH_KEY = "auth.v1";

// ── Auth persistence ─────────────────────────────────────────────────────────
function load(): AuthState {
  if (typeof window === "undefined") return { user: null };
  try {
    const raw = localStorage.getItem(AUTH_KEY);
    if (!raw) return { user: null };
    return JSON.parse(raw) as AuthState;
  } catch {
    return { user: null };
  }
}

let state: AuthState = load();
const listeners = new Set<() => void>();

function persist() {
  if (typeof window !== "undefined") {
    localStorage.setItem(AUTH_KEY, JSON.stringify(state));
  }
  listeners.forEach((l) => l());
}

// ── Store ───────────────────────────────────────────────────────────────────
export const authStore = {
  getState: () => state,
  subscribe: (l: () => void) => {
    listeners.add(l);
    return () => listeners.delete(l);
  },

  /** Attempt login via API. Returns the user on success, null on failure. */
  async login(username: string, password: string): Promise<AuthUser | null> {
    try {
      const user = await authApi.login(username, password);
      state = { user: user as AuthUser };
      persist();
      return user as AuthUser;
    } catch {
      return null;
    }
  },

  async logout() {
    await authApi.logout();
    state = { user: null };
    persist();
  },

  /** Initialize auth state from token on app load */
  async init() {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      state = { user: null };
      persist();
      return;
    }
    try {
      const user = await authApi.getMe();
      state = { user: user as AuthUser };
      persist();
    } catch {
      // Token invalid, clear
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      state = { user: null };
      persist();
    }
  },

  // ── User management (admin-only) via API ──────────────────────────────────

  /** Return all stored users (fetches from API). */
  async getUsers(): Promise<AuthUser[]> {
    try {
      const { usersApi } = await import("./api/client");
      return (await usersApi.list()) as AuthUser[];
    } catch {
      return [];
    }
  },

  /** Add a new user via API. Returns the created user. */
  async addUser(data: {
    username: string;
    password: string;
    displayName: string;
    role: Role;
  }): Promise<AuthUser | null> {
    try {
      const { usersApi } = await import("./api/client");
      const user = await usersApi.create(data);
      listeners.forEach((l) => l());
      return user as AuthUser;
    } catch {
      return null;
    }
  },

  /** Update a user's role via API. */
  async updateUserRole(userId: string, role: Role): Promise<AuthUser | null> {
    try {
      const { usersApi } = await import("./api/client");
      await usersApi.updateRole(userId, role);
      // If the current logged-in user was changed, update state too
      if (state.user?.id === userId) {
        state = { ...state, user: { ...state.user, role } };
        persist();
      }
      listeners.forEach((l) => l());
      return { ...state.user!, role } as AuthUser;
    } catch {
      return null;
    }
  },

  /** Delete a user via API. Cannot delete yourself. */
  async deleteUser(userId: string): Promise<boolean> {
    if (state.user?.id === userId) return false;
    try {
      const { usersApi } = await import("./api/client");
      await usersApi.delete(userId);
      listeners.forEach((l) => l());
      return true;
    } catch {
      return false;
    }
  },
};

// ── React hooks ──────────────────────────────────────────────────────────────
export function useAuth(): AuthState {
  return useSyncExternalStore(
    authStore.subscribe,
    () => state,
    () => ({ user: null }),
  );
}

// ── Permission helpers ──────────────────────────────────────────────────────
/** Minimum role hierarchy (higher = more access). */
const roleHierarchy: Record<Role, number> = {
  viewer: 0,
  waiter: 1,
  cashier: 2,
  manager: 3,
  admin: 4,
};

/** Check whether `user` has at least `minimum` role. */
export function hasRole(user: AuthUser | null, minimum: Role): boolean {
  if (!user) return false;
  return roleHierarchy[user.role] >= roleHierarchy[minimum];
}
